// @flow

import React from "react";
import { connect } from "react-redux";

import * as accountSelectors from "@modules/account/selectors";
import accountActionCreator from "@modules/account/actionCreator";
import type { State } from "@store/reducers";

type OwnProps = $Exact<{
  // http is an Axios instance
  http: *,
  children: *,
}>;

type PropsFromState = $Exact<{
  tokenExpiresAt: ?string,
}>;

type PropsFromDispatch = $Exact<{
  updateTokenExpiresAt: typeof accountActionCreator.SetAccessTokenExpiresAt,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class SessionProvider extends React.Component<Props> {
  http: * = null;
  interceptor: * = null;

  componentDidMount() {
    if (this.props.http) {
      this.setIntercept();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.http && this.props.http !== prevProps.http) {
      this.setIntercept();
    }
  }

  componentWillUnmount() {
    this.clearInterceptor();
  }

  clearInterceptor() {
    if (!this.http || !this.interceptor) {
      return;
    }

    try {
      this.http.interceptors.request.eject(this.interceptor);
    } catch (e) {
      console.warn("Failed to correctly eject http interceptor", e);
    } finally {
      this.http = null;
      this.interceptor = null;
    }
  }

  setIntercept() {
    this.clearInterceptor();

    if (!this.props.http) {
      return;
    }

    try {
      this.http = this.props.http;
      this.interceptor = this.props.http.interceptors.response.use(response => {
        const accessTokenExpiresAt = ((response || {}).headers || {})["access-token-expires-at"];
        if (accessTokenExpiresAt && accessTokenExpiresAt !== this.props.tokenExpiresAt) {
          this.props.updateTokenExpiresAt(accessTokenExpiresAt);
        }

        return response;
      });
    } catch (e) {
      console.warn("Failed to set http interceptor", e);
      this.http = null;
      this.interceptor = null;
    }
  }

  render() {
    return <React.Fragment>{this.props.children}</React.Fragment>;
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    tokenExpiresAt: accountSelectors.getTokenExpiresAt(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  updateTokenExpiresAt: accountActionCreator.SetAccessTokenExpiresAt,
};

export default connect(mapStateToProps, mapDispatchToProps)(SessionProvider);
